import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchMe, exchangeGoogleCode, logout as apiLogout } from "@/lib/api";
import { getUserId } from "@/lib/userId";

const AuthContext = createContext(null);

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
// The redirect_uri MUST be configured under Authorized redirect URIs in
// Google Cloud Console → Credentials → our OAuth client. Each environment
// (preview / production / local) needs its own entry. We always derive
// from window.location.origin so the same code runs everywhere.
const googleRedirectUri = () => `${window.location.origin}/auth/google`;

/**
 * Custom Google OAuth — the user's consent screen shows "Kanji Build"
 * (the consent screen registered against our own GOOGLE_CLIENT_ID).
 *
 *   1. Sign in: redirect to `https://accounts.google.com/o/oauth2/v2/auth`
 *      with our client_id + redirect_uri = {origin}/auth/google + scopes.
 *   2. Google bounces the user back to `{origin}/auth/google?code=<X>&state=...`.
 *   3. The AuthProvider effect below detects that pathname, POSTs the
 *      code to /api/auth/google, the backend swaps it with Google's token
 *      endpoint, fetches the user info, and mints our httpOnly cookie.
 *   4. On success we navigate("/") so the user lands inside the app.
 *
 * Anonymous → authenticated MIGRATION still flows through the same
 * server-side handler — we always pass `getUserId()` as `anonymous_user_id`
 * so progress recorded before login re-keys to the new user_id.
 */
export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // status: 'idle' | 'exchanging' | 'authed' | 'anonymous'
  const [status, setStatus] = useState("idle");

  // Detect a Google OAuth callback. Google redirects the user back to
  // `{origin}/auth/google?code=...&state=...`. We swap the code with the
  // backend, then navigate to "/" so the user lands inside the app.
  useEffect(() => {
    if (location.pathname !== "/auth/google") return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const errorParam = params.get("error");
    if (errorParam) {
      console.warn("Google OAuth returned error:", errorParam);
      setStatus("anonymous");
      navigate("/welcome", { replace: true });
      return;
    }
    if (!code) return;
    setStatus("exchanging");
    exchangeGoogleCode(code, googleRedirectUri(), getUserId())
      .then((data) => {
        setUser(data.user);
        setStatus("authed");
        // Land the user inside the app. The Welcome page's own redirect
        // would also do this, but going straight to "/" skips a render.
        navigate("/", { replace: true });
      })
      .catch((err) => {
        console.warn("Google OAuth exchange failed:", err?.response?.data || err);
        setStatus("anonymous");
        navigate("/welcome", { replace: true });
      });
    // We intentionally depend only on the pathname — re-triggering on
    // navigate/location-search would cause the exchange to fire twice
    // and the second call would 401 (Google codes are single-use).
  }, [location.pathname]);

  // On mount, ask the backend who we are (cookie-based).
  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((u) => { if (!cancelled) { setUser(u); setStatus("authed"); } })
      .catch(() => { if (!cancelled) setStatus("anonymous"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const signIn = useCallback(() => {
    // Redirect to Google's OAuth consent screen with our branded client.
    // The user sees "Kanji Build wants to access your account" — not
    // "Emergent". Scopes are the minimum needed: openid + email + profile.
    if (!GOOGLE_CLIENT_ID) {
      console.warn("REACT_APP_GOOGLE_CLIENT_ID missing — auth misconfigured.");
      return;
    }
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: googleRedirectUri(),
      response_type: "code",
      scope: "openid email profile",
      // `select_account` lets the user pick which Google account to use
      // even if they're already signed in elsewhere. Without it Google
      // sometimes auto-picks the wrong account.
      prompt: "select_account",
      access_type: "online",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  }, []);

  const signOut = useCallback(async () => {
    // We deliberately swallow logout errors — the user has clicked
    // "sign out" and we want them signed out client-side regardless
    // of whether the server cleanup succeeded. The next /auth/me
    // call will reconcile state. Logged for debuggability.
    try { await apiLogout(); } catch (e) {
      console.warn("apiLogout failed; clearing client session anyway", e);
    }
    setUser(null);
    setStatus("anonymous");
    // Refresh so all per-user TanStack queries (progress, mnemonics,
    // etc.) re-fetch under the anonymous user_id.
    window.location.reload();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, status, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
